import { useAuth } from "@cavort/auth";
import { RiArrowLeftLine, RiLoader4Line } from "@remixicon/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { OrderTotal } from "@/components/orders/order-total";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { formatBerlin } from "@/lib/datetime";
import {
	formatMoney,
	type ProhandelState,
	prohandelLabel,
	prohandelTone,
	statusLabel,
	statusTone,
} from "@/lib/order-status";
import { isOrgAdmin } from "@/lib/role";

export const Route = createFileRoute("/_app/orders/$orderId")({
	component: OrderDetailPage,
});

interface OrderLine {
	id: string;
	// CSV-only fields: NULL on EVENTS-sourced orders (migration 041 relaxed
	// these NOT-NULL constraints; barcode+size stay NOT NULL).
	glbOrderLineId: string | null;
	barcode: string;
	sku: string | null;
	size: string;
	lineStatus: number | null;
	fullPrice: string | null;
	totalQuantity: number | null;
	fulfilledAt: string | null;
	stockWritebackMethod: string | null;
	stockWritebackAt: string | null;
}

interface OrderDetail {
	id: string;
	// CSV-only fields: NULL on EVENTS-sourced orders (migration 041 relaxed
	// these NOT-NULL constraints - see backend/src/shapes/farfetch_orders.rs).
	orderNumber: string | null;
	orderCode: string;
	siteId: string | null;
	prohandelState: string;
	siteAccount: string | null;
	branchNumber: number | null;
	fulfillmentStatus: string;
	fulfillmentNotes: string | null;
	cancelReason: string | null;
	step: number | null;
	status: number | null;
	latestTrigger: string | null;
	numberProducts: number | null;
	currency: string | null;
	totalWithShip: string | null;
	customerNickname: string | null;
	customerEmail: string | null;
	customerAddress1: string | null;
	customerAddress2: string | null;
	customerZip: string | null;
	customerCity: string | null;
	customerState: string | null;
	customerCountry: string | null;
	dateCreated: string;
	lines: OrderLine[];
	/** Total converted to EUR; null when no rate is available. */
	totalEur: number | null;
	/** Every store mail this order produced, sent or not. */
	notifications: {
		id: string;
		storeName: string | null;
		kind: string;
		recipients: string[];
		bcc: string | null;
		subject: string;
		sentAt: string | null;
		error: string | null;
		createdAt: string;
	}[];
	/**
	 * Per-barcode ERP state. `prohandelState` is one word for the whole order,
	 * so on a split order it cannot say WHICH barcode is stuck -- and
	 * `saleError` had no surface at all, leaving a failed sale booking looking
	 * like a healthy "moved" order.
	 */
	reservations: OrderReservation[];
}

interface OrderReservation {
	id: string;
	barcode: string;
	size: string;
	quantity: number;
	fromBranch: number;
	toBranch: number;
	status: string;
	requestedAt: string | null;
	movedAt: string | null;
	issuedAt: string | null;
	saleError: string | null;
	stockShortfall: number | null;
	stockCheckedAt: string | null;
}

interface BranchRow {
	number: number;
	name: string;
	isActive: boolean;
}

/**
 * The per-barcode state behind the order's roll-up Prohandel badge.
 *
 * Each row carries its own branches, so this is where `prohandelLabel`'s
 * route-aware form ("Moved 34 -> 600") finally has the data to render -- the
 * order-level badge only ever had one branch and fell back to the generic
 * "Moved to 600".
 */
function ReservationRow({ reservation: r }: { reservation: OrderReservation }) {
	// Mirrors the backend's roll-up precedence: a shortfall outranks progress,
	// because the order cannot be picked as it stands whatever its state says.
	// A released row is history, not a live hold, so it never reads as an alarm.
	const state: ProhandelState =
		r.status === "released"
			? "none"
			: r.stockShortfall != null && r.stockShortfall > 0 && r.issuedAt == null
				? "stock_short"
				: r.issuedAt != null
					? "issued"
					: r.movedAt != null
						? "moved"
						: r.requestedAt != null
							? "requested"
							: "reserved";

	return (
		<div className="rounded-md border px-2 py-1.5 text-xs">
			<div className="flex items-center justify-between gap-2">
				<span className="font-mono">
					{r.barcode}
					{r.size ? ` / ${r.size}` : ""}
					{r.quantity > 1 ? ` x${r.quantity}` : ""}
				</span>
				<Badge variant={r.status === "released" ? "outline" : prohandelTone(state)}>
					{r.status === "released" ? "Released" : prohandelLabel(state, r.fromBranch, r.toBranch)}
				</Badge>
			</div>
			{r.saleError && (
				<p className="mt-1 text-destructive" role="alert">
					Sale booking failed: {r.saleError}
				</p>
			)}
		</div>
	);
}

function OrderDetailPage() {
	const { orderId } = useParams({ from: "/_app/orders/$orderId" });
	const qc = useQueryClient();
	const [reassignOpen, setReassignOpen] = useState(false);
	const [reassignBranch, setReassignBranch] = useState<string>("");
	// Reassigning moves real stock between branches and mails a real shop, so
	// the backend requires OrgAdmin. Hide the control rather than let a member
	// click it into a 403. The page itself stays readable by members.
	const { user } = useAuth();
	const canReassign = isOrgAdmin(user);

	const orderKey = ["farfetch", "order", orderId] as const;

	const query = useQuery({
		queryKey: orderKey,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/api/v1/farfetch/orders/{id}" as never,
				{ params: { path: { id: orderId } } } as never,
			);
			if (error) throw new Error("failed to load order");
			return data as OrderDetail;
		},
	});

	const branches = useQuery({
		queryKey: ["prohandel", "branches"],
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/api/v1/prohandel/branches" as never);
			if (error) throw new Error("failed to load branches");
			return data as BranchRow[];
		},
	});

	// The fulfill / cannot-fulfill mutations were removed with their buttons
	// (2026-08-28). Their routes still exist on the backend for any script that
	// used them; the UI no longer offers a by-hand path that the Prohandel sale
	// booking now covers.
	const reassign = useMutation({
		mutationFn: async (branchNumber: number) => {
			const { error } = await fetchClient.POST(
				"/api/v1/farfetch/orders/{id}/reassign-branch" as never,
				{ params: { path: { id: orderId } }, body: { branchNumber } } as never,
			);
			if (error) throw new Error("reassign failed");
		},
		onSuccess: () => {
			toast.success("Branch reassigned");
			setReassignOpen(false);
			qc.invalidateQueries({ queryKey: orderKey });
		},
		onError: () => toast.error("Could not reassign branch"),
	});

	if (query.isLoading) {
		return (
			<div data-testid="order-detail-skeleton" className="space-y-4">
				<Skeleton className="h-8 w-48" />
				<Skeleton className="h-32 w-full" />
				<Skeleton className="h-48 w-full" />
			</div>
		);
	}
	if (query.error || !query.data) {
		return (
			<div
				role="alert"
				className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-2"
			>
				<p className="font-medium">Could not load this order.</p>
				<Button type="button" variant="outline" size="sm" onClick={() => query.refetch()}>
					Retry
				</Button>
			</div>
		);
	}

	const order = query.data;
	const branchOptions = (branches.data ?? []).filter((b) => b.isActive);
	// A cancelled or fully fulfilled order has nothing left to route, and
	// reassigning one would release a hold (or reverse an ERP movement) for an
	// order that is already closed.
	const isTerminal =
		order?.fulfillmentStatus === "cancelled" || order?.fulfillmentStatus === "fulfilled";

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-2">
				<Button asChild variant="ghost" size="sm">
					<Link to="/orders">
						<RiArrowLeftLine className="size-4" />
						Orders
					</Link>
				</Button>
			</div>

			<div className="flex items-center justify-between">
				<div>
					{/* Farfetch's own number leads (BRS189179599): it is what Burresi and
					    Farfetch support both quote. The ShippingOrder code (SO-6JZYR3) is
					    internal to this system, so it moves to the subtitle - still shown,
					    because it is the key for anything we log or query ourselves. */}
					<h1 className="text-2xl font-bold tracking-tight">
						Order {order.orderNumber ?? order.orderCode}
					</h1>
					<p className="text-muted-foreground font-mono text-sm">{order.orderCode}</p>
				</div>
				<Badge variant={statusTone(order.fulfillmentStatus)}>
					{statusLabel(order.fulfillmentStatus, order.branchNumber)}
				</Badge>
			</div>

			<div className="grid gap-4 md:grid-cols-2">
				<Card>
					<CardHeader>
						<CardTitle>Fulfillment</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2 text-sm">
						<div className="flex justify-between">
							{/* Site ID was org-level config, identical on every order. The
							    Prohandel movement state is what actually differs per order. */}
							<span className="text-muted-foreground">Prohandel</span>
							<Badge variant={prohandelTone(order.prohandelState as ProhandelState)}>
								{prohandelLabel(order.prohandelState as ProhandelState)}
							</Badge>
						</div>
						{/* Optional-chained on purpose: the deployed OpenAPI schema
						    predates this field, and an older cached response must
						    render the page rather than crash it. */}
						{(order.reservations?.length ?? 0) > 0 && (
							<div className="space-y-1 pt-1">
								{order.reservations.map((r) => (
									<ReservationRow key={r.id} reservation={r} />
								))}
							</div>
						)}
						<div className="flex justify-between items-center">
							<span className="text-muted-foreground">Branch</span>
							<span className="flex items-center gap-2">
								{order.branchNumber ?? "unassigned"}
								{canReassign && !isTerminal && (
									<Dialog
										open={reassignOpen}
										onOpenChange={(open) => {
											setReassignOpen(open);
											if (!open) setReassignBranch("");
										}}
									>
										<DialogTrigger asChild>
											<Button variant="outline" size="sm">
												Reassign
											</Button>
										</DialogTrigger>
										<DialogContent>
											<DialogHeader>
												<DialogTitle>Reassign branch</DialogTitle>
												<DialogDescription>
													Pick the branch that will fulfill this order. Stock is re-verified.
												</DialogDescription>
											</DialogHeader>
											<div className="space-y-2">
												<Label>Branch</Label>
												<Select value={reassignBranch} onValueChange={setReassignBranch}>
													<SelectTrigger>
														<SelectValue placeholder="Select branch" />
													</SelectTrigger>
													<SelectContent>
														{branchOptions.map((b) => (
															<SelectItem key={b.number} value={String(b.number)}>
																{b.number} - {b.name || "(no name)"}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</div>
											<DialogFooter>
												<DialogClose asChild>
													<Button variant="outline">Cancel</Button>
												</DialogClose>
												<Button
													onClick={() => reassign.mutate(Number(reassignBranch))}
													disabled={!reassignBranch || reassign.isPending}
												>
													{reassign.isPending && (
														<RiLoader4Line className="mr-1 size-4 animate-spin" />
													)}
													Reassign
												</Button>
											</DialogFooter>
										</DialogContent>
									</Dialog>
								)}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="text-muted-foreground">Total</span>
							<OrderTotal
								totalWithShip={order.totalWithShip}
								currency={order.currency}
								totalEur={order.totalEur}
							/>
						</div>
						{order.cancelReason && (
							<div className="flex justify-between">
								<span className="text-muted-foreground">Cancel reason</span>
								<span>{order.cancelReason}</span>
							</div>
						)}
					</CardContent>
				</Card>

				{order.customerNickname && (
					<Card>
						<CardHeader>
							<CardTitle>Customer</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2 text-sm">
							<div>{order.customerNickname}</div>
							{/* The shipping address, which is what goes on the parcel. Rendered
							    as separate lines rather than one joined string so a missing
							    field leaves a gap instead of a stray comma. */}
							{order.customerAddress1 && <div>{order.customerAddress1}</div>}
							{order.customerAddress2?.trim() && <div>{order.customerAddress2}</div>}
							{(order.customerZip || order.customerCity) && (
								<div>{[order.customerZip, order.customerCity].filter(Boolean).join(" ")}</div>
							)}
							{(order.customerState || order.customerCountry) && (
								<div className="text-muted-foreground">
									{[order.customerState, order.customerCountry]
										.filter(Boolean)
										.filter((v, i, a) => a.indexOf(v) === i)
										.join(", ")}
								</div>
							)}
							{order.customerEmail && (
								<div className="pt-1 font-mono text-xs text-muted-foreground">
									{order.customerEmail}
								</div>
							)}
						</CardContent>
					</Card>
				)}
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Line items</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="border rounded-md overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Barcode</TableHead>
									<TableHead>SKU</TableHead>
									<TableHead>Size</TableHead>
									<TableHead className="text-right">Qty</TableHead>
									<TableHead className="text-right">Price</TableHead>
									{/* Writeback and the fulfill/cannot-fulfill buttons were removed
									    2026-08-28. They belonged to the manual-checklist model, which
									    the Prohandel sale booking replaced: `writeback` only ever
									    showed "-" because nothing wrote it, and with accept/decline
									    paused the two buttons offered actions no operator should take
									    by hand. The order's real progress is the six-step bar on the
									    list, driven by store_stock_reservation. */}
								</TableRow>
							</TableHeader>
							<TableBody>
								{order.lines.map((l) => (
									<TableRow key={l.id}>
										<TableCell className="font-mono text-xs">{l.barcode}</TableCell>
										<TableCell className="font-mono text-xs">{l.sku ?? "-"}</TableCell>
										<TableCell>{l.size}</TableCell>
										<TableCell className="text-right">{l.totalQuantity ?? "-"}</TableCell>
										<TableCell className="text-right">
											{formatMoney(l.fullPrice, order.currency)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
					{order.fulfillmentNotes && (
						<div className="mt-3 text-xs text-muted-foreground whitespace-pre-line">
							{order.fulfillmentNotes}
						</div>
					)}
				</CardContent>
			</Card>

			{/* The mail log. Reads from `store_order_notification`, which records a
			    row per (order, store) BEFORE the send and stamps sent_at only once
			    SMTP accepted it.

			    This exists because "we never got the mail" had no answer inside the
			    app: on 2026-08-26/27 six orders' copy recipients were silently
			    dropped and the only trace was in journald. A row with sent_at NULL
			    and an error is now visible to the operator who is actually asking. */}
			<Card>
				<CardHeader>
					<CardTitle>Benachrichtigungen</CardTitle>
				</CardHeader>
				<CardContent>
					{order.notifications.length === 0 ? (
						// A real state, not an error: an order whose branch belongs to no
						// store has nobody to mail, and orders are also dispatched by
						// other means.
						<p className="text-sm text-muted-foreground">
							Für diese Bestellung wurde keine E-Mail an einen Laden erzeugt.
						</p>
					) : (
						<div className="border rounded-md overflow-hidden">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Status</TableHead>
										<TableHead>Laden</TableHead>
										<TableHead>Art</TableHead>
										<TableHead>Empfänger</TableHead>
										<TableHead>Kopie</TableHead>
										<TableHead>Betreff</TableHead>
										<TableHead>Zeitpunkt</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{order.notifications.map((n) => (
										<TableRow key={n.id}>
											<TableCell>
												{n.sentAt ? (
													<Badge variant="secondary">Gesendet</Badge>
												) : n.error ? (
													<Badge variant="destructive">Fehlgeschlagen</Badge>
												) : (
													// Recorded but never handed to SMTP - e.g. email
													// disabled. Distinct from a failure, because nothing
													// was attempted.
													<Badge variant="outline">Nicht gesendet</Badge>
												)}
											</TableCell>
											<TableCell>{n.storeName ?? "-"}</TableCell>
											<TableCell className="text-xs text-muted-foreground">
												{KIND_LABELS[n.kind] ?? n.kind}
											</TableCell>
											<TableCell className="font-mono text-xs">
												{n.recipients.length > 0 ? n.recipients.join(", ") : "-"}
											</TableCell>
											<TableCell className="font-mono text-xs text-muted-foreground">
												{n.bcc ?? "-"}
											</TableCell>
											<TableCell className="text-xs">{n.subject}</TableCell>
											<TableCell className="text-xs text-muted-foreground">
												{formatBerlin(n.sentAt ?? n.createdAt)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
					{/* The failure reason belongs under the table, not in a tooltip: it is
					    the one thing an operator needs in order to act. */}
					{order.notifications
						.filter((n) => n.error)
						.map((n) => (
							<p key={n.id} className="mt-3 text-xs text-destructive">
								{n.storeName ?? "?"}: {n.error}
							</p>
						))}
				</CardContent>
			</Card>
		</div>
	);
}

/** The three mail variants, in the operator's language, not the wire value. */
const KIND_LABELS: Record<string, string> = {
	whole: "Komplette Bestellung",
	split_lead: "Teilbestellung (versendender Laden)",
	split_sender: "Teilbestellung (zuliefernder Laden)",
	// Manual-action alerts to the org copy list (DEV-873). These live in
	// `farfetch_order_action_mail`, not the store-mail table, and the API
	// prefixes their reason with `action_` so this log can tell them apart.
	action_needs_branch: "Alarm: Filiale manuell wählen",
	action_no_single_branch: "Alarm: keine Filiale hat alle Artikel",
	action_partial_stock: "Alarm: Artikel ohne Bestand oder Größe",
};
